<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="fas fa-city" class="q-mb-sm" /> {{ $t('headquarter') }}
		</div>

		<div class="row gutter-sm">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="140">
					<q-input
						v-model="name"
						:float-label="
							current_client === 'chevrolet' ? $t('group_name') : $t('name')
						"
						:error="$v.name.$error"
						@input="$v.name.$touch"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-field :count="140">
					<q-input
						v-model="fantasy"
						:float-label="
							current_client === 'chevrolet'
								? $t('economic_group')
								: $t('fantasy_name')
						"
						:error="$v.fantasy.$error"
						@input="$v.fantasy.$touch"
					/>
				</q-field>
			</div>

			<div v-if="hasDocument" class="col-xs-12 col-sm-12">
				<q-field
					:error="document_error"
					:error-label="document_error ? $t('document_not_unique') : ''"
				>
					<q-input
						v-model="document"
						:float-label="
							current_client === 'chevrolet'
								? $t('cd_matriz')
								: $t('company_document')
						"
						:error="$v.document.$error"
						@input="$v.document.$touch"
						@blur="documentUnique()"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					filter
					autofocus-filter
					v-model="company_type"
					:options="company_types"
					:float-label="$t('organization_type')"
					:error="$v.company_type.$error"
					@input="$v.company_type.$touch"
				/>
			</div>
		</div>

		<q-list class="q-my-md q-pb-md">
			<q-collapsible icon="far fa-map" :label="$t('address')" v-model="has_address" dense>
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<q-select
							v-model="country"
							:options="countries"
							:float-label="$t('country')"
							:error="$v.country.$error"
							@input="$v.country.$touch"
						/>
					</div>

					<div v-if="keyGooogleMaps" class="col-xs-12 col-sm-12">
						<q-field
							:error="show_maps_alert"
							:error-label="
								$t(
									'not_found_check_that_the_search_is_spelled_correctly_and_try_to_add_a_street_address_and_the_number_of_valid_addresses'
								)
							"
						>
							<q-input
								v-model="address"
								:float-label="$t('address')"
								autocomplete="new-password"
								:error="$v.address.$error"
								@input="$v.address.$touch"
								:placeholder="$t('search_by_name_and_street_number')"
							>
								<q-autocomplete
									@search="getAddressGoogleMaps"
									:min-characters="3"
									@selected="selectedAddressGoogleMaps"
									separator
								/>
							</q-input>
						</q-field>
					</div>

					<div v-else class="col-xs-12 col-sm-12">
						<q-input
							v-model="address"
							:float-label="$t('address')"
							autocomplete="new-password"
							:error="$v.address.$error"
							@input="$v.address.$touch"
						>
							<q-autocomplete
								@search="getAddress"
								:min-characters="3"
								@selected="selectedAddress"
								separator
							/>
						</q-input>
					</div>

					<div class="col-xs-12 col-sm-4">
						<q-input v-model="number" :float-label="$t('number')" ref="number" />
					</div>

					<div class="col-xs-12 col-sm-4">
						<q-input v-model="complement" :float-label="$t('complement')" />
					</div>

					<div class="col-xs-12 col-sm-4">
						<q-input v-model="neighborhood" :float-label="$t('neighborhood')" />
					</div>

					<div class="col-xs-12 col-sm-4">
						<q-input v-model="postal_code" :float-label="$t('postal_code')" />
					</div>

					<div class="col-xs-12 col-sm-4">
						<q-input
							v-model="city"
							:float-label="$t('city')"
							:error="$v.city.$error"
							@input="$v.city.$touch"
						/>
					</div>

					<div class="col-xs-12 col-sm-4">
						<q-input
							v-model="state"
							:float-label="$t('state')"
							:error="$v.state.$error"
							@input="$v.state.$touch"
						/>
					</div>
				</div>
			</q-collapsible>
		</q-list>

		<div class="row gutter-sm">
			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('status') }}</p>
				<q-btn-toggle v-model="status" :toggle-color="getColorStatus" :options="statuses" />
			</div>
		</div>

		<div class="row gutter-sm justify-end q-my-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'admin_headquarters' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>

		<div class="q-display-1 q-mb-md text-secondary" v-if="id">
			<q-icon name="far fa-building" class="q-mb-sm" /> {{ $t('branches') }}
		</div>

		<q-table
			:data="branches"
			:columns="visibleBranchColumns"
			:pagination.sync="server_pagination"
			@request="fetchBranches"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
			v-if="id"
		>
			<template slot="top" slot-scope="props">
				<q-search
					v-model="filter"
					icon="fas fa-search"
					:placeholder="$t('search')"
					:debounce="500"
				/>
				<div class="col" />
				<q-btn
					color="secondary"
					icon="fas fa-plus"
					:label="$t('new_f')"
					@click="
						current_client === 'chevrolet'
							? $router.push({
									name: 'admin_branch',
									params: { id: 0, id_company: id, company_document: name },
							  })
							: $router.push({
									name: 'admin_branch',
									params: { id: 0, id_company: id, company_document: document },
							  })
					"
					:disable="disableNewBranch"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="name" :props="props">
					{{ props.row.name }}
				</q-td>

				<q-td key="document" :props="props">
					{{ props.row.document }}
				</q-td>

				<q-td key="branch_type" :props="props">
					{{ getBranchType(props.row.branch_type) }}
				</q-td>

				<q-td v-if="hasBranchCategory" key="branch_size_type" :props="props">
					{{ props.row.branch_size_type_label }}
				</q-td>

				<q-td key="status" :props="props" auto-width>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatusBranches(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="fas fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_branch',
								params: { id: props.row.actions, id_company: id },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>
					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { debounce } from 'quasar'
import { OpenStreetMapProvider } from 'leaflet-geosearch'
const provider = new OpenStreetMapProvider({
	params: {
		addressdetails: '1',
	},
})

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'organization'] },

	data() {
		return {
			id: '',
			name: '',
			fantasy: '',
			document: '',
			company_type: '',
			address: '',
			number: '',
			complement: '',
			neighborhood: '',
			postal_code: '',
			lat: '',
			long: '',
			city: '',
			state: '',
			country: '',
			status: 1,
			statuses: [
				{ label: this.$t('active'), value: 1 },
				{ label: this.$t('inactive'), value: 2 },
				{ label: this.$t('awaiting_validation'), value: 4 },
			],
			all_profiles: false,
			document_error: false,
			has_address: false,
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			loading: false,
			branches: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
				},
				{
					name: 'name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'name',
					sortable: true,
				},
				{
					name: 'document',
					required: true,
					label: 'document',
					align: 'left',
					field: 'document',
					sortable: true,
				},
				{
					name: 'branch_type',
					required: true,
					label: 'branch_type',
					align: 'left',
					field: 'branch_type',
					sortable: true,
				},
				{
					name: 'branch_size_type',
					required: true,
					label: 'branch_size_types',
					align: 'left',
					field: 'branch_size_type_label',
					sortable: false,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'center',
					field: 'status',
					sortable: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			action_id: '',
			filter: '',
			confirm_remove: false,
			countries: [],
			current_client: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			company_types: 'registration_lists/company_types',
			branch_types: 'registration_lists/branch_types',
			user_clients: 'common/user_clients',
			id_user_client_selected: 'common/id_user_client_selected',
			clients: 'params/clients',
		}),

		hasBranchCategory() {
			const clientParams = this.clients.find(
				(client) => String(client.id) === String(this.id_user_client_selected)
			)
			return !!clientParams && clientParams.has_branch_category === true
		},

		visibleBranchColumns() {
			return this.columns.filter(
				(column) => column.name !== 'branch_size_type' || this.hasBranchCategory
			)
		},

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getColorStatus() {
			if (this.status == 1) return 'positive'
			else if (this.status == 4) return 'warning'
			else return 'negative'
		},

		addressRequired() {
			if (this.address || this.city || this.state || this.country || this.lat || this.long)
				return true
			else return false
		},

		keyGooogleMaps() {
			return process.env.KEY_GOOGLE_API
		},

		disableNewBranch() {
			if (this.current_client === 'chevrolet') {
				if (this.status === 1 && this.name.length) return false
				else return true
			} else {
				if (this.status === 1 && this.document.length) return false
				else return true
			}
		},

		hasDocument() {
			let obj = this.clients.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) return obj.company_use_document
			else return false
		},
	},

	methods: {
		fetchData() {
			this.$axios
				.get(`/admin_companies/companies/${this.id}/`, this.getHeader)
				.then((response) => {
					this.name = response.data.name
					this.fantasy = response.data.fantasy
					this.document = response.data.document
					this.company_type = response.data.company_type
					this.status = response.data.status

					if (response.data.address) {
						this.has_address = true

						this.address = response.data.address.address
						this.postal_code = response.data.address.postal_code
						this.city = response.data.address.city
						this.state = response.data.address.state
						this.lat = response.data.address.lat
						this.long = response.data.address.long
						this.neighborhood = response.data.address.neighborhood
						this.number = response.data.address.number
						this.complement = response.data.address.complement
						this.country = response.data.address.country
					}

					this.$q.loading.hide()

					this.fetchBranches({ pagination: this.server_pagination })
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		fetchBranches({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.$axios
				.get(
					`/admin_companies/companies/${this.id}/branches/?${this.$qs.stringify(data)}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.branches = []
					response.data.results.forEach((result) => {
						this.branches.push({
							id: result.id,
							name: result.name,
							fantasy: result.fantasy,
							document: result.document,
							branch_type: result.branch_type,
							branch_size_type: result.branch_size_type,
							branch_size_type_label: result.branch_size_type_label,
							status: result.status,
							actions: result.id,
						})
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				if (!this.hasDocument) this.document = null

				let address = {
					address: this.address,
					number: this.number,
					complement: this.complement,
					postal_code: this.postal_code,
					city: this.city,
					state: this.state,
					neighborhood: this.neighborhood,
					lat: this.lat,
					long: this.long,
					country: this.country,
				}

				let fd = {
					address: address,
					company_type: this.company_type,
					document: this.document,
					fantasy: this.fantasy,
					name: this.name,
					status: this.status,
				}

				if (this.id != 0) {
					this.$axios
						.put(`/admin_companies/companies/${this.id}/`, fd, this.getHeader)
						.then((response) => {
							this.$q.loading.hide()
							this.$router.push({ name: 'admin_headquarters' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					this.$axios
						.post(`/admin_companies/companies/`, fd, this.getHeader)
						.then((response) => {
							this.$q.loading.hide()
							this.id = response.data.id
							this.$router.push({
								name: 'admin_headquarter',
								params: { id: response.data.id },
							})
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				}
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		getAddress(terms, done) {
			provider.search({ query: terms }).then((result) => {
				done(result)
			})
		},

		selectedAddress(item) {
			this.$nextTick(() => (this.address = item.raw.address.road))

			this.postal_code = item.raw.address.postcode
			this.city = item.raw.address.city
			this.state = item.raw.address.state
			this.lat = item.raw.lat
			this.long = item.raw.lon

			if (item.raw.address.neighbourhood) {
				this.neighborhood = item.raw.address.neighbourhood
			} else if (item.raw.address.suburb) {
				this.neighborhood = item.raw.address.suburb
			} else if (item.raw.address.city_district) {
				this.neighborhood = item.raw.address.city_district
			}

			this.$nextTick(() => this.$refs.number.$el.focus())
		},

		getAddressGoogleMaps(terms, done) {
			this.$axios
				.get(
					`https://maps.googleapis.com/maps/api/geocode/json?address=${terms}&key=${
						this.keyGooogleMaps
					}`
				)
				.then((response) => {
					if (response.data.results.length > 0) {
						this.option_address = response.data.results
						let teste = response.data.results.map((e) => ({
							label: e.formatted_address,
							value: e.formatted_address,
							place_id: e.place_id,
						}))
						done(teste)
					} else {
						this.show_maps_alert = true
					}
				})
				.catch((e) => {
					console.log(e)
				})
		},

		selectedAddressGoogleMaps(item) {
			this.show_maps_alert = false
			this.address = ''
			this.number = ''
			this.neighborhood = ''
			this.postal_code = ''
			this.city = ''
			this.state = ''
			this.option_address.map((x) => {
				if (x.place_id === item.place_id) {
					this.lat = x.geometry.location.lat
					this.long = x.geometry.location.lng
					x.address_components.map((e) => {
						switch (e.types[0]) {
							case 'route':
								this.address = e.long_name
								break
							case 'street_number':
								this.number = e.long_name
								break
							case 'political':
							case 'administrative_area_level_4':
								this.neighborhood = e.long_name
								break
							case 'postal_code':
								this.postal_code = e.long_name
								break
							case 'administrative_area_level_2':
								this.city = e.long_name
								break
							case 'administrative_area_level_1':
								this.state = e.long_name
								break
						}
					})
				}
			})
		},

		documentUnique() {
			if (this.document) {
				let fd = new FormData()
				fd.append('document', this.document)
				fd.append('id_company', this.id)

				this.$axios
					.post(
						`/admin_companies/companies/check_document_existance/`,
						fd,
						this.getHeader
					)
					.then((response) => {
						if (response.data.exists) this.document_error = true
						else this.document_error = false
					})
					.catch((e) => {
						console.log(e)
					})
			} else this.document_error = false
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_companies/branches/${this.action_id}/`, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()

					this.fetchBranches({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getBranchType(id) {
			let branch_type = this.branch_types.find((obj) => {
				return obj.value === id
			})
			if (branch_type) return branch_type.label
			else return ''
		},

		getStatus(status) {
			if (status == 1) return 'active'
			else if (status == 2) return 'inactive'
			else if (status == 3) return 'deleted'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
		},

		getColorStatusBranches(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else if (status == 3) return 'negative'
		},

		getCountryList() {
			this.countries = []

			if (this.user_clients) {
				let obj = this.user_clients.find((obj) => {
					return obj.id == this.id_user_client_selected
				})

				if (obj) {
					obj.countries.forEach((country) => {
						this.countries.push({
							label: country.name,
							value: country.id,
						})
					})
				}
			}
		},

		getClientName(id) {
			let obj = this.user_clients.find((obj) => {
				return obj.id === id
			})
			if (obj) return obj.name
			else return ''
		},

		enableCustomClientCheck(client) {
			if (this.getClientName(client) === 'Chevrolet') this.current_client = 'chevrolet'
			else this.current_client = ''
		},
	},

	created() {
		this.enableCustomClientCheck(this.id_user_client_selected)

		this.id = this.$route.params.id
		if (this.id != 0) this.fetchData()
		this.getCountryList()
	},

	validations() {
		return {
			name: { required, maxLength: maxLength(140) },
			fantasy: { required, maxLength: maxLength(140) },
			document: {
				requiredIf: requiredIf(() => {
					return this.hasDocument
				}),
				documentUnique() {
					if (!this.document_error) return true
					else return false
				},
			},
			company_type: { required },
			address: {
				requiredIf: requiredIf(() => {
					return this.addressRequired
				}),
			},
			city: {
				requiredIf: requiredIf(() => {
					return this.addressRequired
				}),
			},
			state: {
				requiredIf: requiredIf(() => {
					return this.addressRequired
				}),
			},
			country: {
				requiredIf: requiredIf(() => {
					return this.addressRequired
				}),
			},
		}
	},
}
</script>

<style lang="stylus" scoped>
@import '~variables'

.file-url
    a
        text-decoration none
        color black
</style>
